import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Helmet } from "react-helmet-async";
import { Trophy } from "lucide-react";
import { AdSlot } from "@/components/ads/AdSlot";
import { toast } from "sonner";

const Invite = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [leagueName, setLeagueName] = useState<string | null>(null);
  const [leagueId, setLeagueId] = useState<string | null>(null);
  const [isMember, setIsMember] = useState<boolean | null>(null);
  const [joining, setJoining] = useState(false);

  // Load league preview (name + id) so user sees what they're joining.
  useEffect(() => {
    if (!code) return;
    supabase.rpc("get_league_preview_by_code", { _code: code }).then(({ data }) => {
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.name) setLeagueName(row.name as string);
      if (row?.id) setLeagueId(row.id as string);
    });
  }, [code]);

  // Check if already a member of this league; if so, toast and auto-redirect.
  useEffect(() => {
    if (!user || !leagueId) return;
    supabase.rpc("is_league_member", { _league: leagueId, _user: user.id }).then(({ data }) => {
      const member = !!data;
      setIsMember(member);
      if (member) {
        toast.info(
          leagueName ? t("invitePage.alreadyMember", { name: leagueName }) : t("invitePage.alreadyMemberGeneric")
        );
        sessionStorage.removeItem("pendingInvite");
        navigate(`/league/${leagueId}`, { replace: true });
      }
    });
  }, [user, leagueId, leagueName, navigate, t]);

  // Stash invite code so /auth can redirect back even if the query param is dropped.
  useEffect(() => {
    if (code) sessionStorage.setItem("pendingInvite", code);
  }, [code]);

  const handleJoin = async () => {
    if (!code) return;
    setJoining(true);
    const { data: returnedLeagueId, error } = await supabase.rpc("join_league_with_code", { _code: code });
    setJoining(false);
    if (error) {
      const msg = (error.message ?? "").toLowerCase();
      if (msg.includes("already a member")) {
        toast.info(
          leagueName ? t("invitePage.alreadyMember", { name: leagueName }) : t("invitePage.alreadyMemberGeneric")
        );
        if (leagueId) navigate(`/league/${leagueId}`);
        return;
      }
      toast.error(t("invitePage.invalid"));
      navigate("/");
      return;
    }
    if (!returnedLeagueId) {
      toast.error(t("invitePage.invalid"));
      navigate("/");
      return;
    }
    sessionStorage.removeItem("pendingInvite");
    navigate(`/league/${returnedLeagueId}`);
  };

  const authHref = `/auth?redirect=${encodeURIComponent(`/invite/${code}`)}`;
  const authSignupHref = `${authHref}&tab=signup`;

  const subtitle = leagueName
    ? t("invitePage.subtitleNamed", { name: leagueName })
    : t("invitePage.subtitleGeneric");

  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <Helmet>
        <title>{t("invitePage.title")} — Tipomatic</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="absolute top-3 right-3"><LanguageSwitcher /></div>
      <div className="container max-w-md px-4 py-10">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2 text-2xl font-bold">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-gold">
            <Trophy className="h-5 w-5" />
          </div>
          {t("auth.title")}
        </Link>

        <Card className="text-foreground">
          <CardContent className="pt-6 space-y-4 text-center">
            <h1 className="text-xl font-semibold">{t("invitePage.title")}</h1>
            <p className="text-muted-foreground">{subtitle}</p>

            {loading || (user && isMember === null) ? (
              <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
            ) : user ? (
              isMember ? (
                <div className="space-y-3 pt-2">
                  <p className="text-sm text-muted-foreground">
                    {leagueName
                      ? t("invitePage.alreadyMember", { name: leagueName })
                      : t("invitePage.alreadyMemberGeneric")}
                  </p>
                  <Button className="w-full h-11 bg-gradient-pitch" onClick={() => navigate(`/league/${leagueId}`)}>
                    {t("invitePage.goToLeague")}
                  </Button>
                </div>
              ) : (
                <Button className="w-full h-11 bg-gradient-pitch" disabled={joining} onClick={handleJoin}>
                  {joining ? t("invitePage.joining") : t("invitePage.joinBtn")}
                </Button>
              )
            ) : (
              <div className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">{t("invitePage.loginRequired")}</p>
                <Button asChild className="w-full h-11 bg-gradient-pitch">
                  <Link to={authHref}>{t("invitePage.signInToJoin")}</Link>
                </Button>
                <Button asChild variant="outline" className="w-full h-11">
                  <Link to={authSignupHref}>{t("invitePage.signUpToJoin")}</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="mt-6">
          <AdSlot placement="invite-bottom" />
        </div>
      </div>
    </div>
  );
};

export default Invite;
